import {
  ChangeAction,
  TaskType,
  TaskWithColor
} from '@components/calendar/calendar-store'
import {createUseStore} from '@containers/hooks'
import {makeAutoObservable, observable} from 'mobx'
import {DataObjectId, TaskData} from 'yougile-model'

interface State {
  enabled: boolean
  initialX: number
  initialY: number
  xTraveled: number
  yTraveled: number
  parentWidth: number
  deadline: Date
  startDate: Date
  placeholderDate: Date
  draggedItem: {
    id: DataObjectId
    changeAction: ChangeAction
    taskType: TaskType
    height: number
    width: number
    top: number
    backgroundColor: TaskWithColor['columnColor']
    gtdStage: TaskData['gtdStage']
    title: TaskData['title']
    isColorDark: TaskWithColor['isColorDark']
    assigned: TaskData['assigned']
    isSubtask: TaskWithColor['isSubtask']
  }
  showDrag: boolean
  hoveredItemId: DataObjectId
}

export class DraggableStore {
  state: State = {
    enabled: false,
    initialX: 0,
    initialY: 0,
    xTraveled: 0,
    yTraveled: 0,
    parentWidth: 0,
    deadline: null,
    startDate: null,
    placeholderDate: null,
    draggedItem: {
      id: null,
      changeAction: null,
      taskType: null,
      height: null,
      width: null,
      top: null,
      backgroundColor: null,
      gtdStage: null,
      title: null,
      isColorDark: null,
      assigned: null,
      isSubtask: null
    },
    showDrag: false,
    hoveredItemId: null
  }

  constructor() {
    makeAutoObservable(this, {
      state: observable.shallow
    })
  }

  setInitial(prevX: number, prevY: number) {
    this.state.initialX = prevX
    this.state.initialY = prevY
  }

  setTraveled(xTraveled: number, yTraveled: number) {
    this.state.xTraveled = xTraveled
    this.state.yTraveled = yTraveled
  }

  setParentWidth(hourWidth: number) {
    this.state.parentWidth = hourWidth
  }

  setDeadline(deadline: Date) {
    this.state.deadline = deadline
  }

  setStartDate(startDate: Date) {
    this.state.startDate = startDate
  }

  setPlaceholderDate(placeholderDate: Date) {
    this.state.placeholderDate = placeholderDate
  }

  setEnabled(enabled: boolean) {
    this.state.enabled = enabled
  }

  setDraggedItem({
    id,
    changeAction,
    taskType,
    height,
    width,
    top,
    backgroundColor,
    gtdStage,
    title,
    isColorDark,
    assigned,
    isSubtask
  }: {
    id: DataObjectId
    changeAction: ChangeAction
    taskType: TaskType
    height: number
    width: number
    top: number
    backgroundColor: string
    gtdStage: TaskData['gtdStage']
    title: TaskData['title']
    isColorDark: TaskWithColor['isColorDark']
    assigned: TaskData['assigned']
    isSubtask: TaskWithColor['isSubtask']
  }) {
    this.state.draggedItem = {
      id,
      changeAction,
      taskType,
      height,
      width,
      top,
      backgroundColor,
      gtdStage,
      title,
      isColorDark,
      assigned,
      isSubtask
    }
  }

  showedDrag(value: boolean) {
    this.state.showDrag = value
  }

  hoveredItemId(id: DataObjectId) {
    this.state.hoveredItemId = id
  }

  clearDraggableState() {
    this.setEnabled(false)
    this.setInitial(null, null)
    this.setTraveled(0, 0)
    this.setParentWidth(0)
    this.setDeadline(null)
    this.setStartDate(null)
    this.setPlaceholderDate(null)
    this.setDraggedItem({
      id: null,
      changeAction: null,
      taskType: null,
      height: null,
      width: null,
      top: null,
      backgroundColor: null,
      gtdStage: null,
      title: null,
      isColorDark: null,
      assigned: null,
      isSubtask: null
    })
    this.showedDrag(false)
    this.hoveredItemId(null)
  }

  get canDragBeEnabled() {
    return (
      Math.abs(this.state.yTraveled) > 15 || Math.abs(this.state.xTraveled) > 15
    )
  }
}

const [DraggableContext, useDraggableStore] = createUseStore(DraggableStore)
export {DraggableContext, useDraggableStore}
